import React, {useContext, useState} from 'react';
import axios from 'axios';
import {Box, Button, IconButton, Tooltip, Typography} from '@mui/material';
import { ContractState } from '../../enums/ContractState';
import {ContractContext} from "../../provider/ContractProvider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GetAppIcon from '@mui/icons-material/GetApp';
import ConfirmActionDialog from "../general/ConfirmActionDialog";
import useConfirmAction from "../../hooks/useConfirmAction";

const DownloadContractButton = () => {
    const { contract, resetContract } = useContext(ContractContext);
    const {openDialog, actionToConfirm, openConfirmDialog, closeConfirmDialog} = useConfirmAction();
    const [downloading, setDownloading] = useState(false);

    const finishContract = async () => {
        await resetContract(ContractState.FINISHED);
    };

    const downloadFinalDocx = async () => {
        setDownloading(true);
        try {
            const response = await axios.get(`/contracts/${contract._id}/final-docx`, {
                responseType: 'blob'
            });
            const filename = getFilenameFromContentDisposition(response.headers['content-disposition'])
                || `${contract.title || 'Arbeitsvertrag'}_final.docx`;
            const url = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            }));
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download final contract DOCX:', error);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box display="flex" alignItems="center" gap={2}>
            <ConfirmActionDialog
                openDialog={openDialog}
                handleCloseDialog={closeConfirmDialog}
                actionToConfirm={actionToConfirm}
                title="Prozess abschließen?"
                content="Möchten Sie den Vertragsprozess wirklich beenden? Der Vertrag wird archiviert und der Status auf 'Fertiggestellt' gesetzt."
            />

            <Tooltip title="Neufassung als Word herunterladen">
                <span>
                    <IconButton
                        onClick={downloadFinalDocx}
                        color="primary"
                        size="large"
                        disabled={downloading}
                        sx={{ border: '1px solid rgba(25, 118, 210, 0.5)' }}
                    >
                        <GetAppIcon />
                    </IconButton>
                </span>
            </Tooltip>

            {contract.state !== ContractState.FINISHED && (
                <Button
                    variant="contained"
                    onClick={() => openConfirmDialog(finishContract)}
                    color="success"
                    size="large"
                    endIcon={<CheckCircleOutlineIcon />}
                    sx={{ paddingX: 3 }}
                >
                    <Typography variant="button" sx={{ fontWeight: 'bold' }}>
                        Prozess Abschließen
                    </Typography>
                </Button>
            )}
        </Box>
    );
};

function getFilenameFromContentDisposition(contentDisposition) {
    if (!contentDisposition) return null;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
    if (utf8Match) return decodeURIComponent(utf8Match[1]);

    const fallbackMatch = contentDisposition.match(/filename="([^"]+)"/);
    return fallbackMatch?.[1] || null;
}

export default DownloadContractButton;
